import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {mergeMap, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {logErrorIfInDev, unrecoverableError} from '../common/error-handling';
import {ProductComponentModel, ProductType, Specification} from '../data/model';
import {AddOnUtilsService} from './add-on-utils.service';
import * as addOnActions from './add-on.actions';
import {SpecificationService} from './services/specification.service';
import {CatalogApiService} from '../data/api/service/catalog-api.service';

@Injectable()
export class AddOnEffects {
  loadAddon$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addOnActions.loadAddon),
      mergeMap(({catalogId}) => {
        return this.catalogApi.getProductsByCatalogId(catalogId).pipe(
          map((addon: ProductComponentModel<ProductType>) => {
            return addOnActions.loadAddonSuccess({addon});
          }),
          catchError(error => {
            return of(addOnActions.loadAddonFailed({catalogId, error}));
          }),
        );
      }),
    );
  });

  loadAddonFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(addOnActions.loadAddonFailed),
        tap(action => {
          this.addOnUtils.handleFailedLoadAddon(action.catalogId);
        }),
      );
    },
    {dispatch: false},
  );

  loadAddOnsFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addOnActions.loadAddOnsFailure),
      map(({error}) => unrecoverableError({error})),
    );
  });

  loadSpecs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addOnActions.loadSpecs),
      switchMap(({productId}) => {
        return this.specificationService
          .getProductSpecifications(productId)
          .pipe(
            map((items: Specification[]) =>
              addOnActions.loadSpecsSuccess({
                specs: items,
                productId: productId,
              }),
            ),
            catchError(() => {
              return of(
                addOnActions.loadSpecsFailure({
                  error: {
                    message: 'Unable to load specifications.',
                  },
                }),
              );
            }),
          );
      }),
    );
  });

  loadVariants$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addOnActions.loadVariants),
      switchMap(({productId}) => {
        return this.specificationService.getProductVariations(productId).pipe(
          map(variants =>
            addOnActions.loadVariantsSuccess({variants, productId}),
          ),
          catchError(() => {
            return of(
              addOnActions.loadVariantsFailed({
                error: {
                  message: 'Unable to load product variations',
                },
              }),
            );
          }),
        );
      }),
    );
  });

  actionFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(addOnActions.loadSpecsFailure, addOnActions.loadVariantsFailed),
        logErrorIfInDev(),
        tap(({error}) => this.toast.critical(error.message)),
      );
    },
    {dispatch: false},
  );

  constructor(
    private actions$: Actions,
    private addOnUtils: AddOnUtilsService,
    private specificationService: SpecificationService,
    private toast: SeFeToastService,
    private catalogApi: CatalogApiService,
  ) {}
}
